import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Catálogo geográfico para los formularios.
 *
 * Los países y las provincias cambian una vez al año: se cachean en la consulta. Las ciudades son
 * 171.000 y no se pueden bajar enteras, así que se buscan contra la API según se escribe.
 */

export interface Country {
  code: string;
  nameEs: string;
  nameEn: string;
  euMember: boolean;
  /** Hay catálogo de códigos postales para este país (hoy, solo España). */
  hasPostalCodes?: boolean;
}

export interface Region {
  code: string;
  name: string;
  parentCode: string | null;
  level: number;
}

export interface CityResult {
  id: number;
  name: string;
  regionCode: string | null;
  provinceCode: string | null;
  population: number;
}

export interface PostalCodeResult {
  code: string;
  municipality: string;
  provinceCode: string | null;
  provinceName: string | null;
}

/** Sugerencias de código postal por prefijo («2801» enseña los de Madrid). */
export function usePostalSearch(countryCode: string | null | undefined, texto: string) {
  const [debounced, setDebounced] = useState(texto);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(texto), 300);
    return () => window.clearTimeout(id);
  }, [texto]);

  return useQuery({
    queryKey: ['geo', 'postal-codes', countryCode, debounced],
    queryFn: () => api<{ data: { code: string; municipality: string; provinceName: string | null }[] }>(
      `/geo/postal-codes?countryCode=${countryCode}&q=${encodeURIComponent(debounced)}&limit=10`
    ),
    enabled: Boolean(countryCode) && /^\d{2,10}$/.test(debounced.trim()),
    staleTime: 24 * 60 * 60 * 1000
  });
}

export function useCountries() {
  return useQuery({
    // `v2`: la respuesta cacheada de antes no traía `hasPostalCodes` y dejaba el campo apagado.
    queryKey: ['geo', 'countries', 'v2'],
    queryFn: () => api<{ data: Country[] }>('/geo/countries'),
    staleTime: 24 * 60 * 60 * 1000
  });
}

/** Provincias/estados del país (nivel 2 del catálogo; en España, las 52 provincias). */
export function useRegions(countryCode: string | null | undefined) {
  return useQuery({
    queryKey: ['geo', 'regions', countryCode],
    queryFn: () => api<{ data: Region[] }>(`/geo/regions?countryCode=${countryCode}&level=2`),
    enabled: Boolean(countryCode),
    staleTime: 24 * 60 * 60 * 1000
  });
}

/** Búsqueda de ciudades con retardo: escribir «Val» no dispara tres peticiones. */
export function useCitySearch(countryCode: string | null | undefined, texto: string) {
  const [debounced, setDebounced] = useState(texto);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(texto), 300);
    return () => window.clearTimeout(id);
  }, [texto]);

  return useQuery({
    queryKey: ['geo', 'cities', countryCode, debounced],
    queryFn: () => api<{ data: CityResult[] }>(
      `/geo/cities?countryCode=${countryCode}&q=${encodeURIComponent(debounced)}&limit=15`
    ),
    enabled: Boolean(countryCode) && debounced.trim().length >= 2,
    staleTime: 60 * 60 * 1000
  });
}
